import { useEffect, useState } from 'react';
import commissionService from '@/services/commissionService';
import type { CommissionSuperAdminDashboard } from '@/types/commission';
import { formatCurrency } from '@/types/commission';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, Users, CheckCircle, Clock } from 'lucide-react';

export default function CommissionSuperDashboardPage() {
  const [dashboard, setDashboard] = useState<CommissionSuperAdminDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await commissionService.getSuperAdminDashboard();
      setDashboard(data);
    } catch (err: any) {
      console.error('Error loading super admin dashboard:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Aucune donnée disponible</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Commissions - Dashboard Global</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Vue d'ensemble des commissions de tous les administrateurs
        </p>
      </div>

      {/* KPIs Globaux */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {/* Total Admins */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Administrateurs</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {dashboard.total_admins}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Avec commissions actives
            </p>
          </CardContent>
        </Card>

        {/* Commissions en attente */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">En attente</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {dashboard.pending_periods_count}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {formatCurrency(dashboard.pending_commissions_amount)}
            </p>
          </CardContent>
        </Card>

        {/* Commissions validées */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Validées</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {dashboard.validated_periods_count}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {formatCurrency(dashboard.validated_commissions_amount)}
            </p>
          </CardContent>
        </Card>

        {/* Commissions payées YTD */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Payées (année)</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(dashboard.paid_commissions_ytd)}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Commissions versées
            </p>
          </CardContent>
        </Card>

        {/* Total YTD */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total (année)</CardTitle>
            <DollarSign className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(dashboard.total_commissions_ytd)}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Toutes commissions
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Liste des administrateurs */}
      <Card>
        <CardHeader>
          <CardTitle>Commissions par Administrateur</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Administrateur
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Taux actuel
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    En attente
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Validées
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Payées (année)
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Total (année)
                  </th>
                </tr>
              </thead>
              <tbody>
                {dashboard.admins_summary.map((admin) => (
                  <tr
                    key={admin.admin_id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {admin.admin_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {admin.admin_email}
                        </div>
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 text-sm">
                      <span className="font-semibold text-blue-600">
                        {admin.current_rate ? `${admin.current_rate}%` : '-'}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4 text-sm">
                      <div className="text-orange-600 font-medium">
                        {formatCurrency(admin.pending_amount)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {admin.pending_count} période{admin.pending_count > 1 ? 's' : ''}
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 text-sm">
                      <div className="text-purple-600 font-medium">
                        {formatCurrency(admin.validated_amount)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {admin.validated_count} période{admin.validated_count > 1 ? 's' : ''}
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 text-sm">
                      <div className="text-green-600 font-medium">
                        {formatCurrency(admin.paid_ytd)}
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 text-sm">
                      <div className="text-gray-900 dark:text-white font-bold">
                        {formatCurrency(admin.total_ytd)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-800 font-bold">
                  <td className="py-3 px-4 text-sm">TOTAL</td>
                  <td className="text-right py-3 px-4 text-sm">-</td>
                  <td className="text-right py-3 px-4 text-sm text-orange-600">
                    {formatCurrency(dashboard.pending_commissions_amount)}
                  </td>
                  <td className="text-right py-3 px-4 text-sm text-purple-600">
                    {formatCurrency(dashboard.validated_commissions_amount)}
                  </td>
                  <td className="text-right py-3 px-4 text-sm text-green-600">
                    {formatCurrency(dashboard.paid_commissions_ytd)}
                  </td>
                  <td className="text-right py-3 px-4 text-sm text-gray-900 dark:text-white">
                    {formatCurrency(dashboard.total_commissions_ytd)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Message si aucun admin */}
      {dashboard.admins_summary.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Aucun administrateur avec commissions
            </h3>
            <p className="text-gray-600">
              Configurez des taux de commission pour les administrateurs
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
